import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"

import { useActivityTypeStatsQuery } from "@/hooks/api/useActivityQuery"
import { ActivityTypeKey, ACTIVITY_TYPE_ORDER } from "@/constants/activity"
import { getActivityColor } from "@/app/activity/list/utils"

import "./activityDistributionWidget.scss"

// API response uses enum names ("training", "cup", ...). Convert to enum value ("T", "C", ...)
// before passing to getActivityColor.
const toEnumValue = (name: string) => ActivityTypeKey[name as keyof typeof ActivityTypeKey] ?? name

export default function ActivityDistributionWidget() {
  const { t } = useTranslation()
  const { data: activityTypeStats } = useActivityTypeStatsQuery()

  const series = useMemo(() => {
    if (!activityTypeStats) return []

    return (
      Object.keys(activityTypeStats)
        .map((typeName) => ({
          name: t(typeName),
          key: toEnumValue(typeName),
          count: activityTypeStats[typeName] ?? 0,
          color: getActivityColor(toEnumValue(typeName)),
        }))
        // 개수 내림차순, 동률이면 타입 표준 순서(T, L, C, F, R, E)
        .sort(
          (a, b) =>
            b.count - a.count ||
            (ACTIVITY_TYPE_ORDER[a.key] ?? 999) - (ACTIVITY_TYPE_ORDER[b.key] ?? 999),
        )
    )
  }, [activityTypeStats, t])

  const total = useMemo(() => series.reduce((sum, item) => sum + item.count, 0), [series])

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: "pie",
        height: 240,
        width: 240,
        backgroundColor: "transparent",
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
        style: { fontFamily: "Pretendard, sans-serif" },
      },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "65%",
          borderWidth: 2,
          borderColor: "#ffffff",
          borderRadius: 0,
          dataLabels: { enabled: false },
          states: { hover: { halo: null } },
        },
      },
      series: [
        {
          type: "pie",
          name: t("activityType"),
          data: series
            .filter((s) => s.count > 0)
            .map((s) => ({ name: s.name, y: s.count, color: s.color })),
        },
      ],
    }),
    [series, t],
  )

  const formatPercent = (count: number) =>
    total === 0 ? "0.0%" : `${((count / total) * 100).toFixed(1)}%`

  return (
    <div className="ActivityDistributionWidget">
      <div className="ActivityDistributionWidget__chart">
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>

      <ul className="ActivityDistributionWidget__legend">
        {series.map((item) => (
          <li key={item.name} className="ActivityDistributionWidget__legend-item">
            <span className="ActivityDistributionWidget__legend-name">
              <span
                className="ActivityDistributionWidget__legend-dot"
                style={{ background: item.color }}
              />
              {item.name}
            </span>
            <span className="ActivityDistributionWidget__legend-count">{item.count}</span>
            <span className="ActivityDistributionWidget__legend-percent">
              {formatPercent(item.count)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
